import React, { useMemo, useState } from "react";
import clsx from "clsx";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { useTheme } from "@mui/material";
import styles from "./Insiders.module.scss";
import { useInsiders } from "../../hooks/useInsiders";
import { useTranslation } from "next-i18next";

type SortDir = "asc" | "desc";
type GaugeLabelKey = "strongBuy" | "buy" | "neutral" | "sell" | "strongSell";

const GAUGE_CX = 105;
const GAUGE_CY = 100;
const GAUGE_INNER_R = 88;
const GAUGE_OUTER_R = 100;
const NEEDLE_BASE_R = 5;
const NEEDLE_LEN = 72;

// Semi-circle sectors for the gauge
const GAUGE_SECTORS = [
    { value: 40, color: "#F25555" },
    { value: 40, color: "#F25555" },
    { value: 40, color: "url(#segmentGradient)" },
    { value: 40, color: "#16BB6F" },
    { value: 40, color: "#16BB6F" },
];

// Compute angle from buy/neutral/sell totals (0..180 mapped to -180..0 rotate)
const calcAngle = (buy: number, neutral: number, sell: number) => {
    const total = buy + neutral + sell;
    if (total === 0) return 90; // center
    const idx = (buy - sell) / total; // -1..+1
    const deg = ((idx + 1) / 2) * 180; // 0..180
    return 180 - Math.round(deg); // convert to rotation baseline
};

const needleLabelFromScale = (v: GaugeLabelKey): GaugeLabelKey => v;

const Needle: React.FC<{ cx: number; cy: number; angle: number }> = ({ cx, cy, angle }) => {
    const { palette } = useTheme();
    const needleColor = palette.mode === "dark" ? "#d9d9d9" : "#343841";
    const bx = cx;
    const by = cy;
    return (
        <g>
            <circle cx={bx} cy={by} r={NEEDLE_BASE_R} fill={needleColor} stroke="none" />
            <path
                d={`M${bx},${by}l${NEEDLE_LEN},0`}
                strokeWidth={2}
                stroke={needleColor}
                fill={needleColor}
                style={{
                    transform: `rotate(-${angle}deg)`,
                    transformOrigin: `${bx}px ${by}px`,
                }}
            />
        </g>
    );
};

export const Insiders = ({ ticker }) => {
    const { data, isLoading, error } = useInsiders({ ticker });
    const { t } = useTranslation("common");

    // ---- Gauge data (aggregated over full yearly sample)
    const { totalBuyAmt, totalSellAmt } = useMemo(() => {
        const arr = data?.yearlyInsiderTransactions || [];
        return {
            totalBuyAmt: arr.reduce((s, m) => s + (m?.buyAmount || 0), 0),
            totalSellAmt: arr.reduce((s, m) => s + (m?.sellAmount || 0), 0),
        };
    }, [data]);

    // Optional: you can also aggregate counts if you prefer counts over amounts
    // const { totalBuyCnt, totalSellCnt } = useMemo(...)

    // Build gauge payload
    const gauge = useMemo(() => {
        const buy = totalBuyAmt;
        const sell = totalSellAmt;
        const neutral = 0; // keep as 0 for insiders gauge

        // Scale label by ratio thresholds
        let scoreScale: GaugeLabelKey = "neutral";
        if (buy === 0 && sell === 0) {
            scoreScale = "neutral";
        } else if (buy >= sell * 1.75) {
            scoreScale = "strongBuy";
        } else if (buy > sell) {
            scoreScale = "buy";
        } else if (sell >= buy * 1.75) {
            scoreScale = "strongSell";
        } else if (sell > buy) {
            scoreScale = "sell";
        }

        const angle = calcAngle(buy, neutral, sell);
        const label = needleLabelFromScale(scoreScale);

        return { buy, neutral, sell, scoreScale, angle, label };
    }, [totalBuyAmt, totalSellAmt]);

    // ---- Table data (as before)
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<SortDir>("asc");

    const rowsAll = useMemo(() => {
        if (!data || !Array.isArray(data.transactions)) return [];
        return data.transactions.map((t, i) => ({
            id: `${t.insiderName || "unknown"}-${t.date || i}-${i}`,
            name: t.insiderName || t("analytics.analystForecasts.insiders.unknown"),
            position: t.position || "—",
            type: t.transaction || "",
            op: t.insiderOperationType || "",
            amount: typeof t.amount === "number" ? t.amount : null,
            shares: typeof t.numberOfShares === "number" ? t.numberOfShares : null,
            date: t.date || "—",
        }));
    }, [data]);

    const rows = useMemo(() => {
        const result = [...rowsAll];
        if (!sortField) return result;

        result.sort((a, b) => {
            const aVal = a[sortField] ?? "";
            const bVal = b[sortField] ?? "";

            if (sortField === "type" || sortField === "op") {
                const rank = (item: any) => {
                    const ty = item.op || item.type || "";
                    if (/sell/i.test(ty) || ty === "NS" || ty === "AS") return 1;
                    if (/discretionary/i.test(ty) || ty === "S") return 2;
                    if (/buy/i.test(ty) || ty === "NB") return 3;
                    return 4;
                };
                const ra = rank(a);
                const rb = rank(b);
                return sortDir === "asc" ? ra - rb : rb - ra;
            }

            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortDir === "asc" ? aVal - bVal : bVal - aVal;
            }
            return sortDir === "asc"
                ? String(aVal).localeCompare(String(bVal))
                : String(bVal).localeCompare(String(aVal));
        });

        return result;
    }, [rowsAll, sortField, sortDir]);

    if (isLoading || error) return null;
    const hasAnyData = gauge.buy + gauge.sell + gauge.neutral > 0 || rows.length > 0;
    if (!hasAnyData) return null;

    const gaugeLabel = (key: GaugeLabelKey) => t(`analytics.analystForecasts.gauges.labels.${key}`);
    const multilineGaugeLabel = (key: GaugeLabelKey) => {
        const parts = gaugeLabel(key).split("\n");
        return parts.map((part, idx) => (
            <React.Fragment key={`${part}-${idx}`}>
                {part}
                {idx < parts.length - 1 && <br />}
            </React.Fragment>
        ));
    };

    return (
        <div className={styles.container}>
            <div className={styles.title}>
                {t("analytics.analystForecasts.insiders.title", { ticker })}
            </div>

            <div className={styles.chart}>
                <ResponsiveContainer width="100%" height={225}>
                    <PieChart>
                        <defs>
                            {/* gradient sector in middle */}
                            <linearGradient id="segmentGradient">
                                <stop offset="0%" stopColor="#F25555" />
                                <stop offset="100%" stopColor="#16BB6F" />
                            </linearGradient>
                        </defs>

                        {/* sectors */}
                        <Pie
                            data={GAUGE_SECTORS}
                            startAngle={180}
                            endAngle={0}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            dataKey="value"
                            stroke="none"
                            paddingAngle={4}>
                            {GAUGE_SECTORS.map((s, i) => (
                                <Cell key={i} fill={s.color} />
                            ))}
                        </Pie>

                        {/* needle as activeShape overlay */}
                        <Pie
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            data={GAUGE_SECTORS}
                            cx={GAUGE_CX}
                            cy={GAUGE_CY}
                            innerRadius={GAUGE_INNER_R}
                            outerRadius={GAUGE_OUTER_R}
                            stroke="none"
                            fill="none"
                            activeIndex={1}
                            activeShape={() => (
                                <Needle cx={GAUGE_CX} cy={GAUGE_CY} angle={gauge.angle} />
                            )}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* indicators under the gauge */}
                <div className={styles.indicators}>
                    <span className={styles.chartLabel}>{multilineGaugeLabel("strongSell")}</span>

                    <span
                        className={clsx(styles.needleLabel, {
                            [styles.sell]: gauge.label === "sell" || gauge.label === "strongSell",
                            [styles.neutral]: gauge.label === "neutral",
                        })}>
                        {gaugeLabel(gauge.label).replace(/\n/g, " ")}
                    </span>

                    <span className={styles.chartLabel}>{multilineGaugeLabel("strongBuy")}</span>
                </div>
            </div>

            {/* score legend */}
            <div className={styles.scoreInfo}>
                <div className={styles.item}>
                    <div className={styles.red}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.insiders.bearish")}
                        <br />
                        <span>{gauge.sell.toLocaleString()}</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.yellow}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.insiders.neutral")}
                        <br />
                        <span>0</span>
                    </div>
                </div>
                <div className={styles.item}>
                    <div className={styles.green}></div>
                    <div className={styles.label}>
                        {t("analytics.analystForecasts.insiders.bullish")}
                        <br />
                        <span>{gauge.buy.toLocaleString()}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
